import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { btnStyle, CARD_INGEST_SPIN_KEYFRAMES } from '../theme';
import ActiveFileProgressRow, { deriveFileState } from './ActiveFileProgressRow';
import OverallProgressSummary from './OverallProgressSummary';

/**
 * Real progress-reporting surface for a running (or just-started) ingest —
 * replaces the old single-blob "Overall" bar + one flickering "currentFile"
 * line. Combines two data sources that already exist in card-ingest.js,
 * neither of which alone tells the full story:
 *
 *  - `progress` (card:offload-progress, passed down as a prop, refreshed
 *    live over IPC) — filesDone/fileCount/bytesDone/totalBytes plus
 *    `activeFiles`, one entry per file currently mid-copy across all
 *    COPY_CONCURRENCY workers (see CardIngestWindow's onProgress listener).
 *
 *  - the job's manifest checkpoint (getJobManifest, the same IPC channel
 *    JobDetailView already polls for finished jobs) — written to disk
 *    roughly once a second as each file's copy+checksum finishes, with real
 *    verified/error per file. `progress.activeFiles` never carries a
 *    finished file's verify outcome (it's removed from activeFiles the
 *    instant its copy+checksum work is done), so failures showing up
 *    *during* a live run — not just in the final completion screen — need
 *    this second source.
 */
export default function IngestProgressPanel({ c, jobId, progress, progressPct, cancelIngest }) {
  const startedAtRef = useRef(Date.now());
  const [elapsedMs, setElapsedMs] = useState(0);
  const [manifestFiles, setManifestFiles] = useState([]);

  useEffect(() => {
    const timer = setInterval(() => setElapsedMs(Date.now() - startedAtRef.current), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!jobId) return undefined;
    let cancelled = false;
    const poll = async () => {
      const result = await window.electronAPI.cardIngest.getJobManifest(jobId);
      if (!cancelled && result?.success) setManifestFiles(result.data?.files || []);
    };
    poll();
    const timer = setInterval(poll, 1500);
    return () => { cancelled = true; clearInterval(timer); };
  }, [jobId]);

  const activeFiles = progress?.activeFiles || [];
  const copyingCount = activeFiles.filter((f) => deriveFileState(f) === 'copying').length;
  const verifyingCount = activeFiles.filter((f) => deriveFileState(f) === 'verifying').length;
  const failedSoFar = manifestFiles.filter((f) => !f.pending && !f.verified);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <style>{CARD_INGEST_SPIN_KEYFRAMES}</style>

      <OverallProgressSummary
        c={c}
        filesDone={progress?.filesDone ?? 0}
        fileCount={progress?.fileCount ?? 0}
        bytesDone={progress?.bytesDone ?? 0}
        totalBytes={progress?.totalBytes ?? 0}
        progressPct={progressPct}
        copyingCount={copyingCount}
        verifyingCount={verifyingCount}
        elapsedMs={elapsedMs}
        phaseLabel={!progress ? 'Starting…' : undefined}
      />

      {/* Up to COPY_CONCURRENCY files copy at once on the main-process side --
          one real row per file actually in flight, each tracking its own
          copying/verifying state, instead of one shared line that used to
          get overwritten by whichever concurrent worker reported last. */}
      {activeFiles.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {activeFiles.map((file) => (
            <ActiveFileProgressRow key={file.relativePath} c={c} file={file} />
          ))}
        </div>
      )}

      {progress?.uploaded && (
        <div style={{ fontSize: '10.5px', color: c.ok }}>Uploaded to remote server</div>
      )}
      {progress?.uploadError && (
        <div style={{ fontSize: '10.5px', color: c.warn }}>Remote upload failed: {progress.uploadError} (local copy still kept)</div>
      )}

      {failedSoFar.length > 0 && (
        <div style={{
          display: 'flex', flexDirection: 'column', gap: '4px', padding: '8px 10px',
          border: `1px solid ${c.err}`, borderRadius: '7px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: 600, color: c.err }}>
            <AlertTriangle size={12} /> {failedSoFar.length} file{failedSoFar.length === 1 ? '' : 's'} failed so far
          </div>
          <div style={{ maxHeight: '120px', overflow: 'auto', display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {failedSoFar.map((f) => (
              <div key={f.relativePath} style={{ fontSize: '10.5px', color: c.err, fontFamily: 'monospace' }}>
                {f.relativePath} — {f.error}
              </div>
            ))}
          </div>
        </div>
      )}

      <button onClick={cancelIngest} style={{ ...btnStyle(c, 'danger'), alignSelf: 'flex-start' }}>
        Cancel
      </button>
    </div>
  );
}
